import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorSummary } from '../../components/FormFeedback';
import { PageFrame } from '../../components/PageFrame';
import type { createLearnerSessionClient } from '../../platform/learner/learner-session-client';
import {
  forgetLearner,
  readRemembered,
  rememberLearner,
  type RememberedLearner,
} from './rememberedLearners';

// Crockford Base32 (already excludes the visually-confusable I/L/O/U) — this
// is a client-side format hint only. The server is the real validator and
// never trusts anything about this normalisation.
const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
// A student's own login code is 8 characters; a one-task activity code is 10.
const CODE_LENGTH = 10;
const LOGIN_CODE_LENGTH = 8;
const QR_FRAGMENT_PATTERN = /^#code=([0-9A-Za-z]{8}(?:[0-9A-Za-z]{2})?)$/;
// How long "Hi GUR!" shows before the work opens, so the child can see the
// right name came up. Only used when the server sent a name back.
const GREETING_MS = 900;

// Matches the server's own no-existence-oracle contract: every failure
// reason collapses into this single generic message. Never invent a more
// specific client-side message than what the server actually returned.
const GENERIC_ERROR =
  "That code didn't work — check it and try again. If it still doesn't work, ask your teacher or grown-up for a new code.";
// The server refused because too many sign-ins came from this internet
// address in a short time (a whole school shares one address). The code
// may well be right, so the child must not be told it is wrong.
const TOO_MANY_TRIES = 'Too many tries. Wait a few minutes and try again.';
// The one other thing the server says: the child's own code is fine, but
// no work is waiting for them right now. This is not a mistake, so it gets
// its own friendly screen (owner 2026-10-03) instead of the red error box.
const NO_WORK_HEADLINE = 'You have no work waiting right now.';
const NO_WORK_NEXT_STEP = 'Ask your teacher or grown-up to set some.';
// A remembered name whose code no longer works (most likely the teacher or
// parent made a new code). The saved name is removed so it can't fail again.
const STALE_REMEMBERED =
  "That saved login didn't work, so it has been removed from this device. Type the login code again — your teacher or grown-up may have made a new one.";

// Where to go once a code works. A personal code can reopen work that is
// already under way (e.g. on another device), so go straight back to it.
function screenForStatus(status: string): string {
  if (status === 'paused') return '/learn/paused';
  if (status === 'help_requested') return '/learn/help';
  if (status === 'started' || status === 'in_progress') return '/learn/focus';
  return '/learn/session';
}

function normalizeCode(raw: string): string {
  return raw
    .toUpperCase()
    .split('')
    .filter((char) => CODE_ALPHABET.includes(char))
    .join('')
    .slice(0, CODE_LENGTH);
}

function studentNameOf(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const name = (value as { studentName?: unknown }).studentName;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

type Source = 'typed' | 'qr' | 'remembered';

// A code that worked but had nothing waiting: kept so "Check again" can ask
// the server once more without the child retyping it.
type NoWork = { name: string | null; code: string; source: Source };

type LearnerClient = ReturnType<typeof createLearnerSessionClient>;

type Props = {
  // askForWork is optional: without it the no-work screen simply has no
  // "Ask for work" button.
  client: Pick<LearnerClient, 'exchangeEntryCode'> &
    Partial<Pick<LearnerClient, 'askForWork'>>;
};

export function StudentStart({ client }: Props) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [remembered, setRemembered] = useState<RememberedLearner[]>(() =>
    readRemembered(),
  );
  // With saved names, the page opens on the name buttons; "Someone else"
  // switches to typing a code.
  const [typing, setTyping] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [greeting, setGreeting] = useState<string | null>(null);
  // After a QR scan the greeting waits for the child to tap Start (owner
  // 2026-10-03): a scan signs in with no typing, so this is the moment to
  // see whose name came up before the work opens. Holds where Start goes.
  const [startTarget, setStartTarget] = useState<string | null>(null);
  const [noWork, setNoWork] = useState<NoWork | null>(null);
  // "Ask for work" (owner 2026-10-03): tells the teacher or grown-up, with
  // no typing. Stays "asked" through "Check again" so it can't be pressed
  // over and over (the server also ignores repeat asks until work is set).
  const [ask, setAsk] = useState<
    'idle' | 'sending' | 'teacher' | 'grown_up' | 'failed'
  >('idle');
  const attemptedQrExchange = useRef(false);
  const greetingTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (greetingTimer.current !== null)
        window.clearTimeout(greetingTimer.current);
    };
  }, []);

  useEffect(() => {
    if (attemptedQrExchange.current) return;
    const match = QR_FRAGMENT_PATTERN.exec(window.location.hash);
    if (!match) return;
    attemptedQrExchange.current = true;
    // Strip the fragment from the visible URL immediately, before anything
    // else runs — the code must not linger in the address bar or history
    // for even one render.
    window.history.replaceState(null, '', window.location.pathname);
    const capturedCode = match[1];
    if (!capturedCode) return;
    const extractedCode = normalizeCode(capturedCode);
    void exchange(extractedCode, 'qr');
    // Intentionally run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function exchange(candidateCode: string, source: Source) {
    setStatus('loading');
    setError(null);
    setGreeting(null);
    const saveIfAsked = (name: string | null) => {
      // Opt-in only, typed codes only, and only a student's own login code.
      if (
        source === 'typed' &&
        rememberMe &&
        name &&
        candidateCode.length === LOGIN_CODE_LENGTH
      )
        setRemembered(rememberLearner(name, candidateCode));
    };
    try {
      const result = await client.exchangeEntryCode(candidateCode);
      const name = studentNameOf(result);
      saveIfAsked(name);
      const next = screenForStatus(result.sessionStatus);
      if (!name) {
        void navigate(next, { replace: true });
        return;
      }
      setGreeting(name);
      // Not when coming back from the no-work screen ("Check again"): the
      // child has already seen their name there.
      if (source === 'qr' && noWork === null) {
        setStatus('idle');
        setStartTarget(next);
        return;
      }
      greetingTimer.current = window.setTimeout(() => {
        void navigate(next, { replace: true });
      }, GREETING_MS);
    } catch (err) {
      setStatus('idle');
      const reason = err instanceof Error ? err.message : '';
      if (reason === 'NO_WORK_WAITING') {
        const name = studentNameOf(err);
        // The code itself is fine, so it is still worth remembering.
        saveIfAsked(name);
        setNoWork({ name, code: candidateCode, source });
        return;
      }
      setNoWork(null);
      if (reason === 'RATE_LIMITED') {
        setError(TOO_MANY_TRIES);
        return;
      }
      if (source === 'remembered' && reason === 'ENTRY_CODE_INVALID') {
        setRemembered(forgetLearner(candidateCode));
        setTyping(true);
        setError(STALE_REMEMBERED);
        return;
      }
      setError(GENERIC_ERROR);
    }
  }

  function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    void exchange(code, 'typed');
  }

  async function askForWork(current: NoWork) {
    if (!client.askForWork) return;
    setAsk('sending');
    try {
      const outcome = await client.askForWork(current.code);
      if (outcome === 'work_waiting') {
        // Work was set in the meantime: open it instead.
        setAsk('idle');
        void exchange(current.code, current.source);
        return;
      }
      setAsk(outcome);
    } catch {
      setAsk('failed');
    }
  }

  function leaveGreeting() {
    setGreeting(null);
    setStartTarget(null);
    setCode('');
    setTyping(true);
  }

  function leaveNoWork() {
    setNoWork(null);
    setAsk('idle');
    setError(null);
    setCode('');
    setTyping(false);
  }

  const choosing = remembered.length > 0 && !typing;
  const busy = status === 'loading';
  // The request went to the adult whose code this is.
  const asked = ask === 'teacher' || ask === 'grown_up';
  const title =
    noWork || startTarget
      ? 'Welcome'
      : choosing
        ? 'Who is practising?'
        : 'Enter your login code';

  return (
    <PageFrame title={title} audience="learner" homeHref="/">
      {greeting && startTarget ? (
        <div className="mt-6 grid gap-6 text-center">
          <p role="status" className="text-4xl font-bold text-forest-800">
            Hi {greeting}!
          </p>
          <div className="grid gap-3">
            <button
              type="button"
              onClick={() => {
                void navigate(startTarget, { replace: true });
              }}
              className="dm-btn-primary min-h-14 text-xl"
            >
              Start
            </button>
            <button
              type="button"
              onClick={leaveGreeting}
              className="min-h-11 justify-self-center rounded-lg px-3 text-sm font-semibold text-muted underline hover:text-ink"
            >
              Not {greeting}? Use a different code
            </button>
          </div>
        </div>
      ) : greeting ? (
        <p
          role="status"
          className="mt-6 text-center text-4xl font-bold text-forest-800"
        >
          Hi {greeting}!
        </p>
      ) : noWork ? (
        <div className="mt-6 grid gap-6 text-center">
          <div role="status" className="grid gap-3">
            {noWork.name && (
              <p className="text-4xl font-bold text-forest-800">
                Hi {noWork.name}!
              </p>
            )}
            <p className="text-xl font-semibold text-ink">{NO_WORK_HEADLINE}</p>
            <p className="text-lg leading-relaxed text-ink-soft">
              {NO_WORK_NEXT_STEP}
            </p>
          </div>
          <div className="grid gap-3">
            {client.askForWork &&
              noWork.code.length === LOGIN_CODE_LENGTH &&
              (asked ? (
                <p
                  role="status"
                  className="rounded-lg bg-sage px-4 py-3 text-lg font-semibold text-forest-800"
                >
                  {ask === 'grown_up'
                    ? 'Your grown-up has been told.'
                    : 'Your teacher has been told.'}
                </p>
              ) : (
                <>
                  {ask === 'failed' && (
                    <p role="alert" className="text-base text-ink-soft">
                      That did not send. Try again.
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={busy || ask === 'sending'}
                    onClick={() => {
                      void askForWork(noWork);
                    }}
                    className="dm-btn-primary"
                  >
                    {ask === 'sending' ? 'Asking…' : 'Ask for work'}
                  </button>
                </>
              ))}
            <button
              type="button"
              disabled={busy || ask === 'sending'}
              onClick={() => {
                void exchange(noWork.code, noWork.source);
              }}
              className={
                client.askForWork && !asked
                  ? 'dm-btn-secondary'
                  : 'dm-btn-primary'
              }
            >
              {busy ? 'Checking…' : 'Check again'}
            </button>
            {/* A quiet link, not a button (owner 2026-10-03: "Not you? Sign
                out" as a full button confused students). It is only for a
                brother or sister on the same device, so it names the child
                it is NOT for. */}
            <button
              type="button"
              disabled={busy}
              onClick={leaveNoWork}
              className="min-h-11 justify-self-center rounded-lg px-3 text-sm font-semibold text-muted underline hover:text-ink"
            >
              {noWork.name
                ? `Not ${noWork.name}? Use a different code`
                : 'Use a different code'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {error && (
            <div className="mt-6">
              <ErrorSummary errors={[error]} />
            </div>
          )}

          {choosing ? (
            <div className="mt-6 grid gap-4">
              <p className="text-lg leading-relaxed text-ink-soft">
                Tap your name to start.
              </p>
              <ul className="grid gap-3">
                {remembered.map((learner) => (
                  <li key={learner.code} className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        void exchange(learner.code, 'remembered');
                      }}
                      className="dm-btn-primary min-h-14 flex-1 text-xl"
                    >
                      {learner.name}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setRemembered(forgetLearner(learner.code));
                      }}
                      aria-label={`Forget ${learner.name} on this device`}
                      className="min-h-11 rounded-lg px-3 text-sm font-semibold text-muted underline hover:text-ink"
                    >
                      Forget
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setTyping(true);
                }}
                className="dm-btn-secondary"
              >
                Someone else — type a login code
              </button>
            </div>
          ) : (
            <>
              <p className="mt-4 text-lg leading-relaxed text-ink-soft">
                Type your login code, or scan the QR code your teacher or
                grown-up gave you.
              </p>
              <form
                onSubmit={handleSubmit}
                className="mt-6 grid gap-4"
                noValidate
              >
                <label className="grid gap-2 font-semibold">
                  Login code
                  <input
                    className="dm-field w-full min-w-0 text-center text-2xl font-semibold uppercase tracking-[0.2em]"
                    value={code}
                    onChange={(event) => {
                      setCode(normalizeCode(event.target.value));
                    }}
                    disabled={busy}
                    maxLength={CODE_LENGTH}
                    autoComplete="off"
                    aria-describedby="entry-code-hint"
                  />
                </label>
                <span
                  id="entry-code-hint"
                  className="text-sm font-normal text-muted"
                >
                  8 letters and numbers (or 10 for a one-off activity code).
                </span>
                <div className="flex items-start gap-3">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    disabled={busy}
                    onChange={(event) => {
                      setRememberMe(event.target.checked);
                    }}
                    className="mt-1 size-5 shrink-0"
                    aria-describedby="remember-me-hint"
                  />
                  <div>
                    <label htmlFor="remember-me" className="font-semibold">
                      Remember me on this device
                    </label>
                    <p id="remember-me-hint" className="text-sm text-muted">
                      Next time, just tap your name. Anyone using this device
                      could then open your practice, so leave this off on shared
                      computers.
                    </p>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="dm-btn-primary"
                >
                  Start
                </button>
              </form>
              {remembered.length > 0 && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    setTyping(false);
                  }}
                  className="mt-4 font-semibold text-forest-800 underline"
                >
                  Back to saved names
                </button>
              )}
            </>
          )}

          {busy && (
            <p role="status" className="mt-4 text-muted">
              Checking your code…
            </p>
          )}
        </>
      )}
    </PageFrame>
  );
}
